import { SessionsPageContext } from "layouts/sessions/context/sessions-context"
import { FilterStatus, sessionsColumns } from "layouts/sessions/utils/const"
import {
    filterSessions,
    sessionSort,
    NormalizeSession,
    threeMonthsBeforeStartOfMonth,
} from "layouts/sessions/utils/functions"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMmsService } from "services/mms/context/MmsServiceContext"
import {
    ExportFilterSession,
    IFormFilterSession,
    SessionInfo,
    SessionsTableColumnsType
} from "types/sessions"
import { SORTORDER, SortOrder } from "types/sort"
import { SubmitHandler } from "react-hook-form"
import { MmsServiceInterface } from "interfaces/mms-service.ts"

export const SessionsPageProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [filteredSessions, setFilteredSessions] = useState<SessionInfo[]>([])
    const [filterValues, setFilterValues] = useState<IFormFilterSession | null>(
        null
    )
    const [exportFilterSession, setExportFilterSession] =
        useState<ExportFilterSession | null>(null)
    const mmsService: MmsServiceInterface = useMmsService()
    const sessionsService = mmsService.getSessionsService()

    const {
        data: rawListSessions,
        sendRequest: fetchSessions,
        success: apiSuccess,
        isLoading
    } = sessionsService.useGetSessions(exportFilterSession)

    const [listSessions, setListSessions] = useState<SessionInfo[]>([])

    /*    const {
        sendRequest: exportSessions,
        success: exportSuccess,
        isLoading: exportLoading,
    } = sessionsService.useGetExportSessions(filterValues)*/

    const defaultFilterValues = useRef<IFormFilterSession>({
        date_from: threeMonthsBeforeStartOfMonth(Date.now()),
        date_to: Number.MAX_SAFE_INTEGER,
        rfid_number: "",
        card_holder: "",
        status: FilterStatus.All,
    }).current
    const [noResultFilter, setNoResultFilter] = useState(false)

    const [sortedSessions, setSortedSessions] = useState<SessionInfo[]>([])
    const [sortOrder, setSortOrder] = useState<SortOrder>(SORTORDER.DESC)
    const [orderBy, setOrderBy] =
        useState<keyof SessionsTableColumnsType>("date")

    const [pageNumber, setPageNumber] = useState(1)
    const [numberOfPages, setNumberOfPages] = useState(0)
    const rowsPerPage = 10

    useEffect(() => {
        fetchSessions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const currentSessionsRows = useMemo(() => {
        return sortedSessions.slice(
            rowsPerPage * (pageNumber - 1),
            rowsPerPage * pageNumber
        )
    }, [sortedSessions, pageNumber])

    useEffect(() => {
        setNumberOfPages(Math.ceil(sortedSessions.length / rowsPerPage))
        setPageNumber(1)
    }, [sortedSessions.length])

    const handleChangePage = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            setPageNumber(page)
        },
        []
    )

    useEffect(() => {
        if (rawListSessions !== null && Array.isArray(rawListSessions)) {
            const normalizedSessions = rawListSessions.map((session) =>
                NormalizeSession(session)
            )
            setListSessions(normalizedSessions)
        }
    }, [rawListSessions])

    useEffect(() => {
        if (exportFilterSession !== null) {
            fetchSessions()
        }
    }, [exportFilterSession])

    useEffect(() => {
        if (!filterValues) setFilteredSessions(listSessions)
        else {
            const filterFn = filterSessions(filterValues)
            const filteredSessionsResult = listSessions.filter(filterFn)
            setFilteredSessions(filteredSessionsResult)
        }
    }, [listSessions, filterValues])

    useEffect(() => {
        const { comparatorFn } = sessionsColumns[orderBy]
        const sortFn = sessionSort(sortOrder, comparatorFn)
        const sortedSessionsResult = [...filteredSessions].sort(sortFn)
        setSortedSessions(sortedSessionsResult)
    }, [filteredSessions, sortOrder, orderBy])

    const onFilterSubmit: SubmitHandler<IFormFilterSession> = useCallback(
        (filterValuesForm) => {
            const trimmed = trimFilterValuesForm(filterValuesForm)
            const isDefault =
                trimmed.date_from === defaultFilterValues.date_from &&
                trimmed.date_to === defaultFilterValues.date_to &&
                trimmed.status === defaultFilterValues.status &&
                trimmed.rfid_number === defaultFilterValues.rfid_number &&
                trimmed.card_holder === defaultFilterValues.card_holder

            if (isDefault) {
                setFilterValues(null)
            } else {
                setFilterValues(trimmed)
            }
        },
        []
    )

    const trimFilterValuesForm = (filterValuesForm: IFormFilterSession) => {
        return {
            date_from: filterValuesForm.date_from,
            date_to: filterValuesForm.date_to,
            rfid_number: filterValuesForm.rfid_number.trim(),
            card_holder: filterValuesForm.card_holder.trim(),
            status: filterValuesForm.status,
        }
    }
    useEffect(() => {
        if (filteredSessions.length === 0 && filterValues) {
            setNoResultFilter(true)
        } else {
            setNoResultFilter(false)
        }
    }, [filteredSessions, filterValues])

    return (
        <SessionsPageContext.Provider
            value={{
                apiSuccess,
                listSessions,
                fetchSessions,
                isLoading,
                filteredSessions,
                sortedSessions,
                currentSessionsRows,
                sortOrder,
                orderBy,
                setSortOrder,
                setOrderBy,
                pageNumber,
                numberOfPages,
                handleChangePage,
                onFilterSubmit,
                defaultFilterValues,
                filterValues,
                noResultFilter,
                setExportFilterSession,
            }}
        >
            {children}
        </SessionsPageContext.Provider>
    )
}
